import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../init-store';
import CoverLayer from '../../shared/components/cover-layer';
import { AutoEnterDocumentPiP } from '../../shared/components/document-pip';
import { ModalDeviceError } from '../../shared/components/modal-device-error';
import { ThemeBackground } from '../../shared/components/theme-background';
import { SELF } from '../../shared/constants';
import { checkHasDesktopAppAction } from '../../shared/features/config';
import {
    isCoverLayerEnabled,
    selectConferenceBackground,
    selectHideNonCallsRelatedInterface,
    selectIsMeetingRoomIntegration,
} from '../../shared/features/config/selectors';
import { selectIsRoomAgentShellEnabled } from '../../shared/features/flags/selectors';
import { goloomSdk } from '../../shared/features/goloom-sdk';
import { clearNotifications, PSNotifications } from '../../shared/features/notifications/ps-notifications';
import { selectInitConnectionMessageReceived } from '../../shared/features/parent-frame-communicator';
import { PremiumButtonHeader } from '../../shared/features/root/root-container/components/default-view/components/premium-button-header';
import { getPremiumButtonEnabled } from '../../shared/features/root/selectors';
import {
    getAudioInputDeviceSelector,
    getDevicesByTypeSelector,
    getVideoInputDeviceSelector,
    refreshDevicesListAction,
    selectIsSettingsReady,
} from '../../shared/features/settings';
import { isMobileUa } from '../../shared/features/uatraits/selectors';
import { ACTION, FEATURE } from '../../shared/helpers/logger-enums';
import { createLogger } from '../../shared/helpers/loggerFacade';
import { countMetrika } from '../../shared/helpers/metrika';
import { useNavigation } from '../../shared/helpers/navigation';
import { useActions } from '../../shared/hooks';
import { useHotKeys } from '../../shared/hooks/hot-keys/use-hot-keys';
import { useOnUnmount } from '../../shared/hooks/useOnUnmount';
import { useUserActivity } from '../../shared/hooks/useUserActivity';
import Overlay, { ToolbarContentProps } from './components/overlay';
import { ConferenceStatus, GRID_VIEW } from './constants';
import { CloudRecordingStopModal } from './features/cloud-recording/components';
import {
    cancelCallAction,
    disconnectConferenceAction,
    joinConference,
    setConferenceStatus,
} from './features/conference/slice';
import { toggleMuteAction } from './features/device/slice';
import { GoloomVideoGrid, setUserPreferredGridViewAction } from './features/goloom-video-grid';
import { selectHasMicrophonePermission } from './features/permissions';
import { selectIsPiPEnabled, selectPiPCloseRequestId, setIsPipOpen } from './features/pip';
import { PiPCall } from './features/pip/components/pip-call';
import { ReportAProblemButton } from './features/report-a-problem';
import { shareScreenAction, unshareScreenAction } from './features/screen-sharing/slice';
import { Sidebar } from './features/sidebar';
import { SummarizationModalsManager } from './features/summarization';
import Toolbar from './features/toolbar';
import css from './index.css';
import {
    getActiveGridView,
    getRoomId,
    selectIsPermissionsExtendedEnabled,
    selectVisibleParticipantsList,
} from './selectors';
import { DisconnectReason } from './types';

const logger = createLogger('call-page');

const mapState = (state: RootState) => {
    const {
        call: {
            conference: { joinedAsGuest, conferenceStatus, participantsByIds },
            fullscreen: { isFullscreen },
            screenSharing: { isDesktopSharingEnabled, desktopSharingStatus },
        },
        settings: { checkDesktopAppExistence },
        config,
        ua: { OSFamily },
        sessionId,
        skipOpenApp,
    } = state;

    const participants = selectVisibleParticipantsList(state);

    return {
        isFullscreen,
        participantsCount: conferenceStatus === ConferenceStatus.JOINED ? participants.length : undefined,
        joinedAsGuest,
        isLinux: OSFamily === 'Linux',
        selfState: participantsByIds[SELF],
        conferenceStatus,
        isDesktopSharingEnabled,
        desktopSharingStatus,
        currentVideoDevice: getVideoInputDeviceSelector(state),
        currentInputDevice: getAudioInputDeviceSelector(state),
        audioInputDevices: getDevicesByTypeSelector(state, 'audioinput'),
        audioOutputDevices: getDevicesByTypeSelector(state, 'audiooutput'),
        videoInputDevices: getDevicesByTypeSelector(state, 'videoinput'),
        isYandexTeamUser: state.user?.isYandexTeamUser,
        activeGridView: getActiveGridView(state),
        sessionId,
        skipOpenApp: skipOpenApp || !checkDesktopAppExistence,
        autoJoin: config.autoJoin,
        isMobile: isMobileUa(state),
    };
};

const connector = connect(mapState, {
    cancel: cancelCallAction,
    toggleMute: toggleMuteAction,
    shareScreen: shareScreenAction,
    unshareScreen: unshareScreenAction,
    joinConference,
    disconnectConference: disconnectConferenceAction,
    checkHasDesktopAppAction,
    setUserPreferredGridView: setUserPreferredGridViewAction,
    setConferenceStatus,
    clearNotifications,
    refreshDevicesListAction,
});

type CallProps = ConnectedProps<typeof connector> & {
    onOverlayHide?: () => void;
    isChatAllowed?: boolean;
};

const Call: FunctionComponent<CallProps> = ({
    isFullscreen,
    participantsCount,
    joinedAsGuest,
    isLinux,
    selfState,
    cancel,
    toggleMute,
    conferenceStatus,
    shareScreen,
    unshareScreen,
    joinConference,
    disconnectConference,
    checkHasDesktopAppAction,
    isDesktopSharingEnabled,
    desktopSharingStatus,
    currentVideoDevice,
    currentInputDevice,
    activeGridView,
    setUserPreferredGridView: setGridViewAction,
    isYandexTeamUser,
    sessionId,
    skipOpenApp,
    onOverlayHide,
    clearNotifications,
    isChatAllowed,
    autoJoin,
    refreshDevicesListAction,
    isMobile,
}: CallProps) => {
    const actions = useActions({ setIsPipOpen });

    const roomId = useSelector(getRoomId);
    const isMeetingRoomIntegration = useSelector(selectIsMeetingRoomIntegration);
    const isRoomAgentShellEnabled = useSelector(selectIsRoomAgentShellEnabled);
    const initConnectionMessageReceived = useSelector(selectInitConnectionMessageReceived);
    const conferenceBackground = useSelector(selectConferenceBackground);
    const isPermissionsExtendedEnabled = useSelector(selectIsPermissionsExtendedEnabled);
    const hasMicrophonePermission = useSelector(selectHasMicrophonePermission);
    const isSettingsReady = useSelector(selectIsSettingsReady);
    const hideNonCallsRelatedInterface = useSelector(selectHideNonCallsRelatedInterface) || isRoomAgentShellEnabled;
    const coverLayerEnabled =
        useSelector(isCoverLayerEnabled) && !hideNonCallsRelatedInterface && !isMeetingRoomIntegration;
    const isPiPEnabled = useSelector(selectIsPiPEnabled);
    const pipCloseRequestId = useSelector(selectPiPCloseRequestId);

    const [isOverlayHidden, setIsOverlayHidden] = useState(!joinedAsGuest);

    const { route } = useNavigation();
    const { id: conferenceId, searchString } = route.params;

    const setGridView = useCallback(
        (view: GRID_VIEW) => {
            setGridViewAction({ gridView: view });
        },
        [setGridViewAction]
    );

    const isMountedRef = useRef(true);

    useOnUnmount(() => {
        isMountedRef.current = false;
    });

    useHotKeys({
        withSpaceUnmute: !isPermissionsExtendedEnabled || hasMicrophonePermission,
    });

    const shouldTryOpenApp = !skipOpenApp && !isLinux && joinedAsGuest && !autoJoin && !isMeetingRoomIntegration;
    const [isAttemptToOpenAppExpired, setIsAttemptToOpenAppExpired] = useState(false);
    const [waitContinueInBrowser, setWaitContinueInBrowser] = useState(shouldTryOpenApp);
    const [shouldShowOverlayPreloader, setShouldShowOverlayPreloader] = useState(shouldTryOpenApp || autoJoin);

    useEffect(() => {
        if (isAttemptToOpenAppExpired || !isSettingsReady) {
            return;
        }

        setIsAttemptToOpenAppExpired(true);
        setShouldShowOverlayPreloader(autoJoin);
        setWaitContinueInBrowser(shouldTryOpenApp);

        if (shouldTryOpenApp) {
            checkHasDesktopAppAction();
        }
    }, [shouldTryOpenApp, checkHasDesktopAppAction, isAttemptToOpenAppExpired, isSettingsReady, autoJoin]);

    const onContinueInBrowser = useCallback(() => {
        setWaitContinueInBrowser(false);
        countMetrika('continue_in_browser', sessionId);
    }, [setWaitContinueInBrowser, sessionId]);

    const shouldRunJoin =
        !joinedAsGuest ||
        !waitContinueInBrowser ||
        autoJoin ||
        (isMeetingRoomIntegration && initConnectionMessageReceived);

    useEffect(() => {
        if (shouldRunJoin) {
            // Проверка, чтобы не событие не отстреливало
            // при создании встречи, а только при подключении
            if (joinedAsGuest) {
                logger.startFeatureDelta({
                    feature: FEATURE.CONNECTION,
                    action: ACTION.JOIN_CONFERENCE,
                    timeoutMS: null,
                });
            }

            joinConference({
                roomId: conferenceId,
                searchString,
            });
            return () => {
                disconnectConference({
                    source: DisconnectReason.PAGE_LEAVE,
                });
            };
        }
    }, [
        shouldRunJoin,
        conferenceId,
        searchString,
        joinConference,
        disconnectConference,
        initConnectionMessageReceived,
        joinedAsGuest,
    ]);

    useEffect(
        () => () => {
            clearNotifications();
        },
        [clearNotifications]
    );

    const isJoining = conferenceStatus === ConferenceStatus.JOINING;
    const isJoined = conferenceStatus === ConferenceStatus.JOINED;

    useEffect(() => {
        if (isJoined) {
            countMetrika('meeting_screen', 'show', activeGridView === 'default' ? 'grid' : 'presenter', roomId);
            countMetrika('meeting_screen', 'session', sessionId);

            setShouldShowOverlayPreloader(false);
            setIsOverlayHidden(true);
            onOverlayHide?.();
        }
    }, [isJoined, roomId, sessionId, activeGridView, onOverlayHide]);

    const videoDeviceRef = useRef<typeof currentVideoDevice>();
    const inputDeviceRef = useRef<typeof currentInputDevice>();

    useEffect(() => {
        if (videoDeviceRef.current?.deviceId !== currentVideoDevice?.deviceId && currentVideoDevice?.label) {
            countMetrika('meeting_screen', 'camera_model', currentVideoDevice?.label, roomId);
        }

        if (inputDeviceRef.current?.deviceId !== currentInputDevice?.deviceId && currentInputDevice?.label) {
            countMetrika('meeting_screen', 'mic_model', currentInputDevice?.label, roomId);
        }

        return () => {
            videoDeviceRef.current = currentVideoDevice;
            inputDeviceRef.current = currentInputDevice;
        };
    }, [
        currentVideoDevice,
        currentVideoDevice?.deviceId,
        currentVideoDevice?.label,
        currentInputDevice,
        currentInputDevice?.deviceId,
        currentInputDevice?.label,
        roomId,
    ]);

    useEffect(() => {
        refreshDevicesListAction();
    }, [refreshDevicesListAction]);

    const isUserActive = useUserActivity(isFullscreen);

    const renderGrid = isJoined || !joinedAsGuest || (autoJoin && isJoining);
    const renderOverlay = joinedAsGuest && !isOverlayHidden && !renderGrid;

    const toolbarContent = ({ centerContent, onClickCancel, isOverlay }: Partial<ToolbarContentProps> = {}) => (
        <Toolbar
            className={cn(css.toolbar, { [css.toolbarActive]: isUserActive })}
            centerContent={centerContent}
            joinedAsGuest={joinedAsGuest}
            isJoining={isJoining}
            participantsCount={participantsCount}
            cancel={cancel}
            toggleMute={toggleMute}
            onClickCancel={onClickCancel}
            state={selfState}
            shareScreen={shareScreen}
            unshareScreen={unshareScreen}
            isDesktopSharingEnabled={isDesktopSharingEnabled}
            desktopSharingStatus={desktopSharingStatus}
            gridView={activeGridView}
            onClickChangeGridView={setGridView}
            isYandexTeamUser={isYandexTeamUser}
            hideDesktopSharingButton={isMobile}
            isOverlay={isOverlay}
        />
    );

    const grid = renderGrid ? (
        <>
            {goloomSdk.session && <GoloomVideoGrid session={goloomSdk.session} />}
            {!hideNonCallsRelatedInterface && !isMeetingRoomIntegration && toolbarContent()}
            {!isMeetingRoomIntegration && !isRoomAgentShellEnabled ? <ReportAProblemButton /> : null}
        </>
    ) : null;

    const isPremiumButtonEnabled = useSelector(getPremiumButtonEnabled);

    const content = hideNonCallsRelatedInterface ? (
        grid
    ) : (
        <>
            {grid}
            {renderOverlay ? (
                <>
                    <Overlay
                        toolbarContent={toolbarContent}
                        roomId={roomId}
                        waitContinueInBrowser={waitContinueInBrowser}
                        onContinueInBrowser={onContinueInBrowser}
                        shouldShowPreloader={shouldShowOverlayPreloader}
                    />
                </>
            ) : null}

            {isChatAllowed && !hideNonCallsRelatedInterface && !isMeetingRoomIntegration ? <PSNotifications /> : null}
            {!hideNonCallsRelatedInterface && !isMeetingRoomIntegration ? (
                <>
                    <ModalDeviceError deviceKind="audioinput" />
                    <ModalDeviceError deviceKind="videoinput" />
                </>
            ) : null}
        </>
    );

    const modals =
        isJoined && !isMeetingRoomIntegration && !isRoomAgentShellEnabled ? (
            <>
                <SummarizationModalsManager />
                <CloudRecordingStopModal />
            </>
        ) : null;

    return (
        <>
            {modals}
            {coverLayerEnabled && !renderGrid && !isRoomAgentShellEnabled ? <CoverLayer /> : null}
            {!renderGrid && !isRoomAgentShellEnabled ? <ThemeBackground /> : null}
            {isPremiumButtonEnabled && renderOverlay && !isRoomAgentShellEnabled && (
                <PremiumButtonHeader className={css.premiumButtonHeader} />
            )}
            <div
                className={cn(css.container, {
                    [css.untouchable]: hideNonCallsRelatedInterface || isMeetingRoomIntegration,
                    [css.hideOverflow]: isJoining || isJoined,
                })}
                style={conferenceBackground ? { backgroundColor: conferenceBackground } : {}}
            >
                {content}
            </div>
            {!isRoomAgentShellEnabled && <Sidebar withChat={isChatAllowed} />}
            {isPiPEnabled && (
                <AutoEnterDocumentPiP closeRequestId={pipCloseRequestId} onOpenChange={actions.setIsPipOpen}>
                    <PiPCall />
                </AutoEnterDocumentPiP>
            )}
        </>
    );
};

export const CallPage = connector(Call);
